import {useEffect, useRef, useCallback, useMemo, useTransition} from 'react';
import {useSelector} from 'react-redux';
import {selectAgencyData} from 'src/selectors/agency';
import {
  fetchEventSource,
  EventStreamContentType,
} from '@microsoft/fetch-event-source';
import {createStore} from './chat-store';
import {useStore} from 'zustand';
import type {
  Part,
  TextPartDelta,
  PartDelta,
  ChatMessage,
  Envelope,
  ChatSnapshot,
  WidgetPart,
  ThinkingPart,
  ActionEvent,
  ActionPart,
} from 'src/components/agentic-ui';
import {useSseSecurity} from 'src/utils/redux-sse';
import uniqueId from 'lodash/uniqueId';


export interface UseChatOptions {
  /**
   * Lazy URL resolver; must return the SSE endpoint at send-time.
   * Useful when the URL depends on data created right before the first
   * send (e.g., thread creation).
   */
  getStreamUrl: (() => Promise<string>) | (() => string);
  headers?: Record<string, string>;
  queryParams?: Record<string, string | number | boolean>;
  initialMessages?: ChatMessage[];
  backoff?: {
    baseMs?: number; // default 1000
    maxMs?: number; // default 15000
    maxAttempts?: number; // default Infinity
    resetOnOpen?: boolean; // default true
  };
  /** Event types that indicate end-of-stream and stop reconnecting. */
  terminationTypes?: string[]; // default ['message.complete','done']
  /** Optional: provide a pre-send context collector */
  collectMessageContext?: () => Promise<Record<string, unknown>>;
  /** Optional: provide an agentic event publisher */
  publishMessageAction?: (action: ActionEvent) => void;
}

export interface UseChat {
  snapshot: ChatSnapshot;
  isPending: boolean;
  store: ReturnType<typeof createStore>;
  hydrate: (messages: ChatMessage[], strategy?: 'merge' | 'replace') => void;
  sendMessage: (
    parts: Part[],
    meta?: Record<string, unknown>,
  ) => Promise<string>;
  resend: (clientId: string) => Promise<void>;
  cancel: (clientId: string) => void;
  clear: () => void;
}

/* ---------------- Constants ---------------- */
const DEFAULT_TERMINATION_TYPES = ['message.complete', 'done'] as const;

/* ---------------- backoff helpers ---------------- */
class RetriableError extends Error {
  retryIn?: number;
}
class FatalError extends Error {}
function computeDelay(
  attempt: number,
  {
    baseMs = 1000,
    maxMs = 15000,
  }: Required<Pick<NonNullable<UseChatOptions['backoff']>, 'baseMs' | 'maxMs'>>,
) {
  return Math.min(maxMs, baseMs * 2 ** attempt);
}
function isTerminatingEnvelope(
  evt: Envelope,
  terminationTypeList: string[],
): boolean {
  if (!evt || !terminationTypeList) {
    return false;
  }

  try {
    // if event type is in termination types list
    if (terminationTypeList.includes(evt.type)) {
      return true;
    }
  } catch {
    /* fall through on any error */
  }

  return false;
}
function withQuery(
  url: string,
  query: Record<string, string | number | boolean>,
): string {
  if (!query || Object.keys(query).length === 0) {
    return url;
  }

  const queryString = Object.entries(query)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join('&');

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${queryString}`;
}

/* ---------------- Hook (LAZY SSE over fetch) ---------------- */
export function useChat(opts: UseChatOptions): UseChat {
  const {
    getStreamUrl,
    headers,
    queryParams,
    initialMessages,
    backoff,
    terminationTypes = DEFAULT_TERMINATION_TYPES as unknown as string[],
    collectMessageContext,
    publishMessageAction,
  } = opts;

  const [isPending, startTransition] = useTransition();
  const {composeHeaders, onOpenAuthCheck} = useSseSecurity();

  const agency = useSelector(selectAgencyData);
  const agencyId = agency?.id;

  const storeRef = useRef(createStore({messages: initialMessages ?? []}));
  const store = storeRef.current;
  const messages = useStore(store, (s) => s.messages);
  const error = useStore(store, (s) => s.error);
  const connectionStatus = useStore(store, (s) => s.connectionStatus);
  const snapshot = useMemo(
    () => ({messages, error, connectionStatus}),
    [messages, error, connectionStatus],
  );

  const ctrlRef = useRef<AbortController | null>(null);
  const activeRef = useRef(false); // stream attempt in-progress
  const terminatedRef = useRef(false); // termination requested by server
  const attemptsRef = useRef(0);
  const maxAttempts = backoff?.maxAttempts ?? Number.POSITIVE_INFINITY;
  const cfg = {
    baseMs: backoff?.baseMs ?? 1000,
    maxMs: backoff?.maxMs ?? 15000,
  } as const;

  // Keep termination types stable without causing effect dependency churn
  const terminationTypesRef = useRef<string[]>(terminationTypes);
  terminationTypesRef.current =
    terminationTypes ?? (DEFAULT_TERMINATION_TYPES as unknown as string[]);

  // Stabilize headers/queryParams without forcing reconnections
  const headersRef = useRef<Record<string, string> | undefined>(headers);
  headersRef.current = headers;

  const queryParamsRef = useRef<
    Record<string, string | number | boolean> | undefined
  >(queryParams);
  queryParamsRef.current = queryParams;

  // Stabilize optional lazy URL resolver
  const getStreamUrlRef = useRef<UseChatOptions['getStreamUrl']>(getStreamUrl);
  getStreamUrlRef.current = getStreamUrl;

  const buildUrl = async (): Promise<string> => {
    const baseUrl = await getStreamUrlRef.current();
    return withQuery(baseUrl, queryParamsRef.current || {});
  };

  const stopStream = useCallback(() => {
    ctrlRef.current?.abort();
    ctrlRef.current = null;
    activeRef.current = false;
    store.getState().setConn('CLOSED');
  }, [store]);

  // Cleanup on unmount: stop any active SSE stream
  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  /* ---------------- sending ---------------- */
  const sendMessage = useCallback(
    async (parts: Part[], meta?: Record<string, unknown>) => {
      const id = uniqueId('temp-message');
      const now = Date.now();

      // gather pre-send data
      const collected = collectMessageContext
        ? await collectMessageContext()
        : undefined;

      const finalParts = parts;
      const finalMeta = meta || {};
      const finalContext =
        collected && Object.keys(collected).length ? collected : undefined;

      const local: ChatMessage = {
        id,
        role: 'user',
        createdAt: now,
        parts: finalParts,
        meta: finalMeta,
        context: {page_context: finalContext},
        status: 'sending',
      };
      store.getState().addLocal(local);

      // POST message and handle SSE stream response
      terminatedRef.current = false;
      const url = await buildUrl();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;
      activeRef.current = true;
      store.getState().setConn('CONNECTING');
      //fix this later if required
      const userMessage = finalParts.find(
        (part) => part.type === 'plain_text',
      )?.data;
      const sseHeaders = composeHeaders(headersRef.current || {});

      fetchEventSource(url, {
        signal: ctrl.signal,
        openWhenHidden: true,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sseHeaders,
          Accept: 'text/event-stream',
          ...(agencyId ? {'x-agency-id': String(agencyId)} : {}),
        },

        body: JSON.stringify({
          user_input: userMessage,
          context: {page_context: finalContext},
        }),
        onopen: async (res: Response) => {
          await onOpenAuthCheck(res);
          const isSSE =
            res.ok &&
            res.headers.get('content-type')?.includes(EventStreamContentType);
          if (isSSE) {
            if (backoff?.resetOnOpen ?? true) {
              attemptsRef.current = 0;
            }
            store.getState().setConn('OPEN');
            return;
          }
          if (res.status >= 400 && res.status < 500 && res.status !== 429) {
            store.getState().markError(id);
            throw new FatalError(`HTTP ${res.status}`);
          }
          if (res.status === 429) {
            const ra = res.headers.get('retry-after');
            const retryIn = ra
              ? isNaN(+ra)
                ? Math.max(0, +new Date(ra) - Date.now())
                : +ra * 1000
              : undefined;
            const e = new RetriableError(`HTTP 429`);
            e.retryIn = retryIn;
            throw e;
          }
          store.getState().markError(id);
          throw new RetriableError(`HTTP ${res.status}`);
        },
        onmessage: (msg: {data?: string}) => {
          if (!msg.data) {
            return;
          }
          try {
            const evt = JSON.parse(msg.data) as Envelope;
            // termination detection
            const isTerm = isTerminatingEnvelope(
              evt,
              terminationTypesRef.current,
            );

            handleMessage(evt, store, startTransition, publishMessageAction);

            if (isTerm) {
              terminatedRef.current = true;
              stopStream(); // abort and set CLOSED
            }
          } catch {
            /* ignore non-JSON */
          }
        },
        onclose: () => {
          store.getState().setConn('CLOSED');
          activeRef.current = false;
          ctrlRef.current = null;
          // For this architecture, stream closes after each message - this is expected
        },
        onerror: (err: unknown) => {
          activeRef.current = false;
          if (terminatedRef.current) {
            throw new FatalError('terminated');
          }
          if (++attemptsRef.current > maxAttempts) {
            store.getState().markError(id);
            throw new FatalError('max-attempts');
          }
          if (err instanceof FatalError) {
            throw err;
          }
          const byHeader =
            err instanceof RetriableError && typeof err.retryIn === 'number'
              ? err.retryIn
              : undefined;
          const delay = byHeader ?? computeDelay(attemptsRef.current - 1, cfg);
          return delay;
        },
      }).catch((e: unknown) => {
        const anyE = e as any;
        if (anyE?.name !== 'AbortError' && !terminatedRef.current) {
          store.getState().setErr({code: 'sse', message: String(anyE)});
          store.getState().markError(id);
        }
      });

      return id;
    },
    [
      store,
      backoff?.resetOnOpen,
      cfg.baseMs,
      cfg.maxMs,
      maxAttempts,
      stopStream,
    ],
  );

  const resend = useCallback(
    async (clientId: string) => {
      const snap = store.getState();
      const m = snap.messages.find(
        (x) => x.id === clientId && x.role === 'user',
      );
      if (!m) {
        return;
      }
      await sendMessage(m.parts, m.meta);
    },
    [store, sendMessage],
  );

  const cancel = useCallback((_clientId: string) => {
    //NOTE:(diwakersurya) (check how cancel will work)
    // fetch(streamUrl, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     ...(headersRef.current || {}),
    //   },
    //   body: JSON.stringify({type: 'cancel', payload: {clientId}}),
    //   keepalive: true,
    // }).catch(() => undefined);
  }, []);

  const clear = useCallback(() => store.getState().clear(), [store]);
  const hydrate = useCallback(
    (msgs: ChatMessage[], strat: 'merge' | 'replace' = 'merge') =>
      store.getState().hydrate(msgs, strat),
    [store],
  );

  return {
    snapshot,
    isPending,
    store,
    sendMessage,
    resend,
    cancel,
    clear,
    hydrate,
  };
}

function handleMessage(
  evt: Envelope,
  store: ReturnType<typeof createStore>,
  startTransition: (callback: () => void) => void,
  publishMessageAction?: (action: ActionEvent) => void,
): void {
  const payload = evt.payload;
  const eventType = evt.type;

  switch (eventType) {
    case 'ack': {
      const {clientId, serverId} = payload as {
        clientId?: string;
        serverId?: string;
      };
      if (clientId) {
        startTransition(() => {
          store.getState().ack(clientId, serverId);
        });
      }
      break;
    }

    case 'user_message': {
      // Replace the client-side user message with the server-side message
      const message = payload as ChatMessage;
      startTransition(() => {
        store.getState().replaceUserMessage(message);
      });
      break;
    }

    case 'message_container': {
      const message = payload as ChatMessage;
      startTransition(() => {
        store.getState().insertMessage(message);
      });
      break;
    }

    case 'message_part': {
      const {id, type, data} = payload as {
        id: string;
        type: Part['type'];
        data: unknown;
      };

      const messages = store.getState().messages;
      const lastMessage = messages[messages.length - 1];
      const lastMessageId = lastMessage?.id;

      if (!lastMessage) {
        return;
      }

      if (type === 'thinking') {
        startTransition(() => {
          // Show loading state while waiting for message parts
          store.getState().insertMessagePart(lastMessageId, {
            id,
            type,
            data: data as ThinkingPart['data'],
          });
        });
      }
      // Handle different part types
      if (type === 'plain_text') {
        startTransition(() => {
          store.getState().insertMessagePart(lastMessageId, {
            id,
            type: 'plain_text',
            data: String(data),
          } as Part);
        });
      } else if (type === 'action') {
        // Broadcast message actions
        try {
          publishMessageAction?.(data as ActionPart['data']);
        } catch {
          /* ignore action publishing errors */
        }
      } else if (type === 'widget') {
        startTransition(() => {
          store.getState().insertMessagePart(lastMessageId, {
            id,
            type: 'widget',
            data: data as WidgetPart['data'],
          } as Part);
        });
      }

      break;
    }

    case 'message_part_delta': {
      const {type, data, part_id} = payload as {
        id: string;
        type: PartDelta['type'];
        data: unknown;
        part_id: string;
      };

      const messages = store.getState().messages;
      const lastMessage = messages[messages.length - 1];
      const lastMessageId = lastMessage?.id;

      if (!lastMessage) {
        return;
      }

      if (type === 'thinking_delta') {
        if (data === 'done') {
          //remove thinking part
          startTransition(() => {
            store.getState().removeMessagePart(lastMessageId, part_id);
          });
        }
      }
      const messagePart = lastMessage.parts.find((part) => part.id === part_id);
      if (!messagePart) {
        return;
      }

      // Handle different part types
      if (type === 'plain_text_delta') {
        startTransition(() => {
          const newMessagePart = {
            ...messagePart,
            data: messagePart.data + String(data),
          } as Part;

          store.getState().updateMessagePart(lastMessageId, newMessagePart);
        });
      }
      if (type === 'thinking_delta') {
        startTransition(() => {
          const newMessagePart = {
            ...messagePart,
            data,
          } as Part;

          store.getState().updateMessagePart(lastMessageId, newMessagePart);
        });
      }
      break;
    }

    case 'error': {
      // URGENT: Keep errors synchronous for immediate user feedback
      const {code, message} = payload as {code?: string; message?: string};
      store.getState().setErr({
        code: code || 'unknown',
        message: message || 'An error occurred',
      });
      break;
    }

    // Ignore other event types (processing, done, etc.)
    default:
      break;
  }
}
